import './Modal.css';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import { getAllUsers } from '../../api/users_institutions';
import { getChapterAdminPermissions } from '../../api/admin_permissions';

const isTempId = (id) => typeof id === 'string' && id.startsWith('temp-');

function ModalChapter({ Action, selectedTopic, selectedChapter, closeModal, chapters, stageCreateTopicContent, stageUpdateTopicContent, stageDeleteTopicContent, stageCreateChapter, stageUpdateChapter, chapterAdminAccess, stageSetChapterAdminAccess }) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedAdminUserIds, setSelectedAdminUserIds] = useState([]);

    // The Chapter's own id (ChapterAdminPermission.chapter FK) -- selectedChapter is the
    // topic-content row, not the Chapter itself, so this resolves through `chapters` like
    // handleUpdateChapter already does. Undefined in Add mode (no chapter exists yet).
    const selectedChapterRowId = chapters.find(c => c.topic_content === selectedChapter?.id)?.id;

    useEffect(() => {
        let cancelled = false;

        const loadAdminUsers = async () => {
            try {
                const users = await getAllUsers();
                const staffUsers = Array.isArray(users) ? users.filter((user) => user.is_staff && !user.is_superuser) : [];
                if (!cancelled) {
                    setAdminUsers(staffUsers);
                }
            } catch (error) {
                console.error('Error loading admin users:', error);
                if (!cancelled) {
                    setAdminUsers([]);
                }
            }
        };

        loadAdminUsers();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        // A selection already staged this session (Level.jsx's chapterAdminAccess map) reflects
        // what the user actually picked -- authoritative over whatever's still in the database,
        // and the only source of truth for a chapter created this session (temp id, no DB row).
        const stagedSelection = selectedChapterRowId !== undefined ? chapterAdminAccess?.[selectedChapterRowId] : undefined;
        if (stagedSelection !== undefined) {
            setSelectedAdminUserIds(stagedSelection);
            return;
        }

        setSelectedAdminUserIds([]);

        const loadSelectedChapterAdminAccess = async () => {
            if (!selectedChapterRowId || isTempId(selectedChapterRowId)) {
                return;
            }

            try {
                const permissions = await getChapterAdminPermissions();
                const userIds = Array.isArray(permissions)
                    ? permissions
                        .filter((permission) => Number(permission.chapter?.id ?? permission.chapter) === Number(selectedChapterRowId))
                        .map((permission) => permission.user?.id ?? permission.user)
                        .filter((id) => id !== undefined && id !== null)
                    : [];

                if (!cancelled) {
                    setSelectedAdminUserIds(userIds);
                }
            } catch (error) {
                console.error('Error loading chapter admin access:', error);
                if (!cancelled) {
                    setSelectedAdminUserIds([]);
                }
            }
        };

        loadSelectedChapterAdminAccess();

        return () => {
            cancelled = true;
        };
    }, [selectedChapterRowId, chapterAdminAccess]);

    const toggleAdminAccessBtn = (userId) => {
        setSelectedAdminUserIds((currentIds) =>
            currentIds.includes(userId)
                ? currentIds.filter((currentId) => currentId !== userId)
                : [...currentIds, userId]
        );
    };

    const renderAdminAccessCheckboxes = () => {
        return adminUsers.map((admin) => (
            <button
                key={admin.id}
                type="button"
                className={`access-btn${selectedAdminUserIds.includes(admin.id) ? ' active' : ''}`}
                data-val={admin.username}
                onClick={() => toggleAdminAccessBtn(admin.id)}
            >
                {admin.first_name || admin.last_name ? `${admin.first_name} ${admin.last_name}`.trim() : admin.username}
            </button>
        ));
    };

    const renderAdminAccessContainer = () => (
        <div id="editModuleAdminAccessContainer" style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
            <label className="label-text">{t('modalChapter.adminAccessLabel')} <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>{t('modalChapter.adminAccessHint')}</span></label>
            <div className="access-btn-group" id="editModuleAdminAccessGroup" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {renderAdminAccessCheckboxes()}
            </div>
        </div>
    );

    const handleCreateChapter = async () => {

        if (!selectedTopic?.id) {
            alert(t('modalChapter.selectTopicFirst'));
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const is_visible = document.getElementById('newChapterVisibility').checked;
            const newTopicContent = await stageCreateTopicContent({
                topic: selectedTopic.id,
                type: 'chapter',
                is_visible: is_visible,
                order: 0,
            });

            const title = document.getElementById('newChapterTitle').value;
            const desc = document.getElementById('newChapterDesc').value;
            const chapterData = {
                title: title,
                description: desc,
                topic_content: newTopicContent.id || null,
            };

            try {
                const newChapter = await stageCreateChapter(chapterData);
                stageSetChapterAdminAccess(newChapter.id, selectedAdminUserIds);
                console.log('Chapter staged:', newChapter);
                closeModal(true);
            } catch (error) {
                console.error('Error staging chapter creation:', error);
            }
        } catch (error) {
            console.error('Error staging topic content for chapter:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateChapter = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const chapter = chapters.find(c => c.topic_content === selectedChapter.id);
        const chapterId = chapter?.id;
        const title = document.getElementById('editChapterTitle').value;
        const desc = document.getElementById('editChapterDesc').value;
        const chapterData = {
            title: title,
            description: desc,
            topic_content: selectedChapter?.id || null,
        };

        let updatedChapter = null;
        try {
            updatedChapter = await stageUpdateChapter(chapterId, chapterData);
            stageSetChapterAdminAccess(chapterId, selectedAdminUserIds);
            console.log('Chapter staged as updated:', updatedChapter);
        } catch (error) {
            console.error('Error staging chapter update:', error);
        }

        const is_visible = document.getElementById('editChapterVisibility').checked;
        // selectedChapter here IS the topic-content row (not the Topic) -- its own type/order,
        // not the topic's, are what this update should carry forward.
        const topicContentData = {
            topic: selectedTopic?.id || null,
            type: selectedChapter?.type || 'chapter',
            is_visible: is_visible,
            order: selectedChapter?.order || 0,
        }
        try {
            const updatedTopicContent = await stageUpdateTopicContent(selectedChapter.id, topicContentData);
            console.log('Topic content staged as updated:', updatedTopicContent);
            closeModal(true);
        } catch (error) {
            console.error('Error staging topic content update:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteChapter = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await stageDeleteTopicContent(selectedChapter.id);
            console.log('Chapter staged as deleted:', selectedChapter.id);
            closeModal(true);
        } catch (error) {
            console.error('Error staging chapter deletion:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
            <div className="Modal">
            {Action === 'Add' ? (
                <div className="modal-box">
                    <h2>{t('modalChapter.addNewChapter')}</h2>
                    <input
                        type="text"
                        id="newChapterTitle"
                        placeholder={t('modalChapter.chapterTitlePlaceholder')}
                    />
                    <input
                        type="text"
                        id="newChapterDesc"
                        placeholder={t('modalChapter.chapterDescPlaceholder')}
                    />
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="newChapterVisibility" defaultChecked={selectedChapter?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {renderAdminAccessContainer()}

                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={handleCreateChapter} disabled={isSubmitting}>
                            {t('common.create')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Update' ? (
                <div className="modal-box">
                    <h2>{t('modalChapter.editChapter')}</h2>
                    <input
                        type="text"
                        id="editChapterTitle"
                        placeholder={t('modalChapter.chapterTitleFieldPlaceholder')}
                        defaultValue={chapters.find(c => c.topic_content === selectedChapter.id)?.title || ''}
                    />
                    <input
                        type="text"
                        id="editChapterDesc"
                        placeholder={t('modalChapter.chapterDescFieldPlaceholder')}
                        defaultValue={chapters.find(c => c.topic_content === selectedChapter.id)?.description || ''}
                    />
                    <div className="toggleVisible" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '15px 0' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{t('common.setVisible')}</div>
                        <label className="switch">
                            <input type="checkbox" id="editChapterVisibility" defaultChecked={selectedChapter?.is_visible}/>
                            <span className="slider" ></span>
                        </label>
                    </div>
                    {renderAdminAccessContainer()}

                    <div className="modal-buttons">
                        <button className="btn-primary" onClick={handleUpdateChapter} disabled={isSubmitting}>
                            {t('common.change')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : Action === 'Delete' ? (
                <div className="modal-box">
                    <h2>{t('modalChapter.deleteChapterTitle')}</h2>
                    <p>{t('modalChapter.deleteChapterConfirm')}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        <strong>{chapters.find(c => c.topic_content === selectedChapter.id)?.title || ''}</strong>
                    </p>
                    <div className="modal-buttons">
                        <button
                            className="btn-primary btn-delete"
                            style={{ background: 'var(--del-color)' }}
                            onClick={handleDeleteChapter}
                            disabled={isSubmitting}
                        >
                            {t('common.delete')}
                        </button>
                        <button
                            className="btn-primary btn-secondary"
                            onClick={closeModal}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
        </>
    );
}

export default ModalChapter;
